import React, { useMemo, useState } from 'react';

const NODE_W = 150, NODE_H = 56;

function edgePath(a, b) {
  // straight or gently curved connector from node a's edge to node b's edge
  const dx = b.x - a.x, dy = b.y - a.y;
  const horizontal = Math.abs(dx) >= Math.abs(dy);
  let x1, y1, x2, y2;
  if (horizontal) { x1 = a.x + (dx > 0 ? NODE_W / 2 : -NODE_W / 2); y1 = a.y; x2 = b.x - (dx > 0 ? NODE_W / 2 : -NODE_W / 2); y2 = b.y; }
  else { x1 = a.x; y1 = a.y + (dy > 0 ? NODE_H / 2 : -NODE_H / 2); x2 = b.x; y2 = b.y - (dy > 0 ? NODE_H / 2 : -NODE_H / 2); }
  if (Math.abs(y2 - y1) < 1 || Math.abs(x2 - x1) < 1) return `M${x1},${y1} L${x2},${y2}`;
  const mx = (x1 + x2) / 2;
  return `M${x1},${y1} C${mx},${y1} ${mx},${y2} ${x2},${y2}`;
}

export default function FlowDiagram({ diagram }) {
  const [active, setActive] = useState(null);
  const byId = useMemo(() => Object.fromEntries(diagram.nodes.map((n) => [n.id, n])), [diagram]);
  const reduce = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const current = active ? byId[active] : null;
  const H = diagram.height;

  return (
    <div className="fd">
      <div className="fd__head">
        <div className="fd__title">{diagram.title}</div>
        <div className="fd__caption">{diagram.caption}</div>
      </div>
      <div className="fd__canvas">
        <svg viewBox={`0 0 1000 ${H}`} className="fd__svg" role="img" aria-label={diagram.title}>
          <defs>
            <marker id="fd-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse"><path d="M0,0 L10,5 L0,10 z" fill="currentColor" /></marker>
            <marker id="fd-arrow-bad" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse"><path d="M0,0 L10,5 L0,10 z" fill="var(--fd-bad)" /></marker>
          </defs>
          {diagram.edges.map((e, i) => {
            const a = byId[e.from], b = byId[e.to];
            const d = edgePath(a, b);
            const lit = active && (e.from === active || e.to === active);
            const dim = active && !lit;
            const id = `fd-e-${diagram.title.length}-${i}`;
            return (
              <g key={i} className={`fd__edge${e.reject ? ' fd__edge--bad' : ''}${lit ? ' lit' : ''}${dim ? ' dim' : ''}`}>
                <path id={id} d={d} markerEnd={e.reject ? 'url(#fd-arrow-bad)' : 'url(#fd-arrow)'} />
                {!reduce && (
                  <circle r="4" className="fd__pulse">
                    <animateMotion dur={`${2.2 + (i % 3) * 0.4}s`} begin={`-${(i * 0.7).toFixed(2)}s`} repeatCount="indefinite" rotate="auto"><mpath href={`#${id}`} /></animateMotion>
                  </circle>
                )}
              </g>
            );
          })}
          {diagram.nodes.map((n) => {
            const isActive = n.id === active;
            const dim = active && !isActive && !diagram.edges.some((e) => (e.from === active && e.to === n.id) || (e.to === active && e.from === n.id));
            return (
              <g key={n.id} className={`fd__node${n.danger ? ' fd__node--bad' : ''}${isActive ? ' active' : ''}${dim ? ' dim' : ''}`}
                transform={`translate(${n.x - NODE_W / 2},${n.y - NODE_H / 2})`}
                onMouseEnter={() => setActive(n.id)} onMouseLeave={() => setActive(null)} onClick={() => setActive(isActive ? null : n.id)} tabIndex={0} onFocus={() => setActive(n.id)} onBlur={() => setActive(null)}>
                <rect width={NODE_W} height={NODE_H} rx="10" />
                <text x={NODE_W / 2} y={24} textAnchor="middle" className="fd__label">{n.label}</text>
                <text x={NODE_W / 2} y={42} textAnchor="middle" className="fd__sub">{n.sub}</text>
              </g>
            );
          })}
          {diagram.edges.map((e, i) => {
            if (!e.label) return null;
            const a = byId[e.from], b = byId[e.to];
            const horizontal = Math.abs(b.x - a.x) >= Math.abs(b.y - a.y);
            const lit = active && (e.from === active || e.to === active);
            const x = horizontal ? (a.x + b.x) / 2 : Math.max(a.x, b.x) + 14;
            const y = horizontal ? Math.min(a.y, b.y) - NODE_H / 2 - 8 : (a.y + b.y) / 2 + 4;
            return <text key={`l${i}`} x={x} y={y} textAnchor={horizontal ? 'middle' : 'start'} className={`fd__elabel${e.reject ? ' bad' : ''}${lit ? ' lit' : ''}${active && !lit ? ' dim' : ''}`}>{e.label}</text>;
          })}
        </svg>
      </div>
      <div className={`fd__desc${current ? ' show' : ''}`}>
        {current ? <><b>{current.label}</b> — {current.desc}</> : <span>Hover a node to see what it does</span>}
      </div>
    </div>
  );
}
